/**
 * "Verify your identity": shown after a risky web sign-in (server:
 * verification_connexion). The server already sent a code (text message to
 * the verified cell phone, otherwise email) and refuses the rest of the API
 * until the code is confirmed. A voice call or an email can be asked instead.
 */
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { isAxiosError } from 'axios'
import { CodeBoxes } from '@/console/ui/CodeBoxes'
import { confirmVerificationCode, fetchVerification, sendVerificationCode } from '@/api/verification'
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { defaultLocale, isLocale } from '@/lib/locales'
import { logout } from '@/lib/auth'
import { tokenNeedsVerification, type Canal } from '@/lib/verificationConnexion'
import { useAuthStore } from '@/stores/useAuthStore'

const LOGO = 'https://sdk.callizy.ca/assets/favicon_callizy.png'
const RESEND_DELAY_S = 30

function errorCode(error: unknown): { code: string; attemptsLeft?: number } {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: string; attempts_left?: number } | undefined
    return { code: data?.error || '', attemptsLeft: data?.attempts_left }
  }
  return { code: '' }
}

export function VerificationPage() {
  const { t } = useTranslation()
  const { locale } = useParams<{ locale: string }>()
  const lng = isLocale(locale) ? locale : defaultLocale
  useDocumentTitle(t('auth.verification.title'))

  const accessToken = useAuthStore((s) => s.accessToken)
  const setTokens = useAuthStore((s) => s.setTokens)
  const postLoginRedirect = usePostLoginRedirect()
  const pending = tokenNeedsVerification(accessToken)

  const [code, setCode] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState('')
  const [expired, setExpired] = useState(false)
  const [canal, setCanal] = useState<Canal | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const leaving = useRef(false)

  // No session: back to sign-in. Already confirmed: carry on as after any sign-in.
  useEffect(() => {
    if (leaving.current) return
    if (!accessToken) {
      leaving.current = true
      window.location.replace(`/${lng}/login`)
    } else if (!pending) {
      leaving.current = true
      void postLoginRedirect(lng)
    }
  }, [accessToken, pending, lng, postLoginRedirect])

  const state = useQuery({
    queryKey: ['verification-connexion'],
    queryFn: fetchVerification,
    enabled: pending,
    retry: false,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (state.data && !canal) setCanal(state.data.canal)
    if (state.isError && ['verification_expired', 'no_pending_verification'].includes(errorCode(state.error).code)) {
      setExpired(true)
    }
  }, [state.data, state.isError, state.error, canal])

  useEffect(() => {
    if (cooldown <= 0) return
    const id = window.setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000)
    return () => window.clearInterval(id)
  }, [cooldown])

  const showError = (err: unknown) => {
    const { code: c, attemptsLeft } = errorCode(err)
    if (c === 'invalid_code' && typeof attemptsLeft === 'number') {
      setError(t('auth.verification.errors.attemptsLeft', { count: attemptsLeft }))
    } else if (c === 'invalid_code') setError(t('auth.verification.errors.invalid'))
    else if (c === 'code_expired') setError(t('auth.verification.errors.expired'))
    else if (c === 'too_many_attempts') setError(t('auth.verification.errors.tooMany'))
    else if (c === 'too_many_requests') setError(t('auth.verification.errors.tooManySends'))
    else if (c === 'send_failed' || c === 'channel_unavailable') setError(t('auth.verification.errors.sendFailed'))
    else if (c === 'verification_expired' || c === 'no_pending_verification') setExpired(true)
    else setError(t('auth.verification.errors.generic'))
  }

  const confirm = useMutation({
    mutationFn: (value: string) => confirmVerificationCode(value, remember),
    onSuccess: async (tokens) => {
      leaving.current = true
      setTokens(tokens.access, tokens.refresh)
      await postLoginRedirect(lng)
      window.dispatchEvent(new Event('login-complete'))
    },
    onError: (err) => {
      setCode('')
      showError(err)
    },
  })

  const send = useMutation({
    mutationFn: (next: Canal) => sendVerificationCode(next),
    onSuccess: (_data, next) => {
      setCanal(next)
      setCode('')
      setError('')
      setCooldown(RESEND_DELAY_S)
      toast.success(t('auth.verification.sentAgain'))
    },
    onError: showError,
  })

  const submit = (value: string) => {
    if (value.length !== 6 || confirm.isPending) return
    setError('')
    confirm.mutate(value)
  }

  const info = state.data
  const current: Canal = canal || info?.canal || 'sms'
  const canaux = info?.canaux || []
  const hasPhone = canaux.includes('sms')
  const busy = confirm.isPending || send.isPending
  const firstSendFailed = info?.envoye === false && !send.isSuccess

  const sentLine = !info
    ? ''
    : current === 'email'
      ? t('auth.verification.sentEmail', { email: info.courriel_masque })
      : current === 'call'
        ? t('auth.verification.sentCall', { digits: info.telephone_fin })
        : t('auth.verification.sentSms', { digits: info.telephone_fin })

  const linkClass =
    'font-medium text-[#0A192F] underline-offset-2 hover:underline disabled:opacity-50 disabled:no-underline dark:text-white'

  return (
    <div className="min-h-dvh bg-slate-50 px-4 py-10 text-slate-900 sm:py-16 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <img src={LOGO} alt="" className="h-7 w-7" />
          <span className="font-semibold">Callizy</span>
        </div>

        <h1 className="text-2xl font-semibold">{t('auth.verification.title')}</h1>

        {expired ? (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('auth.verification.errors.sessionExpired')}</p>
            <button
              type="button"
              onClick={() => logout(`/${lng}/login`)}
              className="w-full rounded-xl bg-[#0A192F] px-4 py-3 text-sm font-medium text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
            >
              {t('auth.verification.signInAgain')}
            </button>
          </div>
        ) : !info ? (
          <div className="mt-10 flex justify-center" aria-busy="true">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <form
            className="mt-2"
            onSubmit={(e) => {
              e.preventDefault()
              submit(code)
            }}
          >
            <p className="text-sm text-slate-600 dark:text-slate-400">{sentLine}</p>

            <div className="mt-6">
              <CodeBoxes
                value={code}
                onChange={(v) => {
                  setCode(v)
                  if (error) setError('')
                }}
                onComplete={submit}
                disabled={confirm.isPending}
                invalid={Boolean(error)}
                label={t('auth.verification.codeLabel')}
                digitLabel={(n) => t('auth.verification.digitLabel', { n })}
              />
            </div>

            <div className="mt-3 min-h-[20px] text-sm" aria-live="polite">
              {error ? (
                <p className="text-red-600 dark:text-red-400">{error}</p>
              ) : firstSendFailed ? (
                <p className="text-red-600 dark:text-red-400">{t('auth.verification.errors.sendFailed')}</p>
              ) : null}
            </div>

            <label className="mt-3 flex cursor-pointer items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-slate-300 accent-[#0A192F]"
              />
              <span>{t('auth.verification.remember')}</span>
            </label>

            <button
              type="submit"
              disabled={code.length !== 6 || confirm.isPending}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0A192F] px-4 py-3 text-sm font-medium text-white hover:bg-[#0A192F]/90 disabled:opacity-50 dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
            >
              {confirm.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {confirm.isPending ? t('auth.verification.checking') : t('auth.verification.continue')}
            </button>

            <p className="mt-6 text-sm text-slate-600 dark:text-slate-400">
              {t('auth.verification.notReceived')}{' '}
              <button
                type="button"
                className={linkClass}
                disabled={busy || cooldown > 0}
                onClick={() => send.mutate(current)}
              >
                {cooldown > 0 ? `${t('auth.verification.resend')} (${cooldown})` : t('auth.verification.resend')}
              </button>
              {hasPhone && current !== 'call' && (
                <>
                  {' '}{t('auth.verification.or')}{' '}
                  <button
                    type="button"
                    className={linkClass}
                    disabled={busy || cooldown > 0}
                    onClick={() => send.mutate('call')}
                  >
                    {t('auth.verification.call')}
                  </button>
                </>
              )}
            </p>

            {(current !== 'email' || hasPhone) && (
              <p className="mt-3 text-sm">
                <button
                  type="button"
                  className={linkClass}
                  disabled={busy}
                  onClick={() => send.mutate(current === 'email' ? 'sms' : 'email')}
                >
                  {current === 'email' ? t('auth.verification.textInstead') : t('auth.verification.otherMethod')}
                </button>
              </p>
            )}

            <p className="mt-10 text-sm">
              <button
                type="button"
                onClick={() => logout(`/${lng}/login`)}
                className="text-slate-500 underline-offset-2 hover:underline dark:text-slate-400"
              >
                {t('auth.verification.signOut')}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
